import { MembershipRoleEnum } from "../../../types/memberships"
import { invariant } from "../../../utils/invariant"
import type { MembershipFormData, ResourceAbility } from "./types"

type NewNoAccessMemberData = {
  firstname: string
  lastname?: string
  sendInvitation: false
  userEmail?: string
}

type NewStandardMemberData = {
  firstname: string
  lastname?: string
  userEmail: string
  abilitiesToAdd: string[]
  abilitiesToRemove: string[]
  sendInvitation: true
  copyPermissionsFrom: MembershipRoleEnum.orgMember
  language: string
}

type NewLimitedMemberData = {
  firstname: string
  lastname?: string
  sendInvitation: true
  userEmail: string
  abilitiesToAdd: string[]
  abilitiesToRemove: string[]
  associations: string[]
  abilitiesInResources: ResourceAbility[]
  copyPermissionsFrom: MembershipRoleEnum.orgLimitedMember
  language: string
}

type NewCopiedMemberData = {
  firstname: string
  lastname?: string
  sendInvitation: true
  userEmail: string
  copyPermissionsFrom: string
  language: string
}

type NewMemberData = NewNoAccessMemberData | NewStandardMemberData | NewLimitedMemberData | NewCopiedMemberData

type MemberType = "noAccess" | "standard" | "limited" | "copied"

export const createNewMember = (formData: MembershipFormData): NewMemberData => {
  const memberType = getMemberType(formData.copyPermissionsFrom)
  switch (memberType) {
    case "noAccess":
      return createNoAccessMember(formData)
    case "standard":
      return createStandardMember(formData)
    case "limited":
      return createLimitedMember(formData)
    case "copied":
      return createCopiedMember(formData)
  }
}

const createNoAccessMember = (formData: MembershipFormData): NewNoAccessMemberData => {
  const firstname = getFirstname(formData)
  const lastname = getLastname(formData)
  invariant(firstname, "Either firstname or lastname must be provided for no access member")

  return {
    firstname,
    lastname,
    sendInvitation: false,
    userEmail: formData.userEmail,
  }
}

const createStandardMember = (formData: MembershipFormData): NewStandardMemberData => {
  const firstname = getFirstname(formData)
  const lastname = getLastname(formData)
  invariant(firstname, "Either firstname or lastname must be provided for standard member")
  invariant(formData.userEmail, "Email must be provided for standard member")

  return {
    firstname,
    lastname,
    userEmail: formData.userEmail,
    abilitiesToAdd: formData.abilitiesToAdd,
    abilitiesToRemove: formData.abilitiesToRemove,
    sendInvitation: true,
    copyPermissionsFrom: MembershipRoleEnum.orgMember,
    language: formData.language,
  }
}

const createLimitedMember = (formData: MembershipFormData): NewLimitedMemberData => {
  const firstname = getFirstname(formData)
  const lastname = getLastname(formData)
  invariant(firstname, "Either firstname or lastname must be provided for limited member")
  invariant(formData.userEmail, "Email must be provided for limited member")

  return {
    firstname,
    lastname,
    sendInvitation: true,
    userEmail: formData.userEmail,
    abilitiesToAdd: formData.abilitiesToAdd,
    abilitiesToRemove: formData.abilitiesToRemove,
    associations: formData.associations,
    abilitiesInResources: formData.abilitiesInResources,
    copyPermissionsFrom: MembershipRoleEnum.orgLimitedMember,
    language: formData.language,
  }
}

const createCopiedMember = (formData: MembershipFormData): NewCopiedMemberData => {
  const firstname = getFirstname(formData)
  const lastname = getLastname(formData)
  invariant(firstname, "Either firstname or lastname must be provided for limited member")
  invariant(formData.userEmail, "Email must be provided for limited member")
  invariant(formData.copyPermissionsFrom, "copyPermissionsFrom must be provided for copied member")

  return {
    firstname,
    lastname,
    sendInvitation: true,
    userEmail: formData.userEmail,
    copyPermissionsFrom: formData.copyPermissionsFrom,
    language: formData.language,
  }
}

const getMemberType = (role?: string): MemberType => {
  if (role === MembershipRoleEnum.orgMember) return "standard"
  if (role === MembershipRoleEnum.orgLimitedMember) return "limited"
  if (typeof role === "string" && role.length > 0) return "copied"
  return "noAccess"
}

const getFirstname = (formData: MembershipFormData): string => {
  return formData.firstname || formData.lastname || ""
}

const getLastname = (formData: MembershipFormData): string | undefined => {
  return getFirstname(formData) === formData.firstname ? formData.lastname : undefined
}
